import { API_DOMAIN } from "@monorepo/constants";
import { LocaleStorageKeysEnum, removeLocalItem } from "@monorepo/helpers";
import {
  IBankPaymentPayload,
  IBankPaymentSuccessResponse,
  ICancelOrderSuccessMutationResponse,
  ICashPaymentSuccessResponse,
  ICreateOrderPayload,
  IHistoricalPaymentSliceState,
  IMIPPaymentSuccessResponse,
  IOrder,
  IProcessPaymenetMutationPayload,
  PaymentIniatorEnum,
  TGetPaymentOptions,
  TProccessPaymentMutationResponse,
} from "@monorepo/types";
import { createApi } from "@reduxjs/toolkit/query/react";

import { customBaseQuery } from "../../helpers/baseQuery";
import { couponsApi } from "../coupons";

const getPaymentUrlByPaymentInitiator = (paymentInitiator: PaymentIniatorEnum) => {
  switch (paymentInitiator) {
    case PaymentIniatorEnum.NEO_PAY:
      return "/api/v1/neopay";
    case PaymentIniatorEnum.KEVIN:
      return "/api/v1/kevin";
    default:
      return "/api/v1/mip";
  }
};

export const orderApi = createApi({
  reducerPath: "orderApi",
  baseQuery: customBaseQuery<
    | ArrayBuffer
    | { data: IOrder | TGetPaymentOptions }
    | { meta: ICashPaymentSuccessResponse | IMIPPaymentSuccessResponse | ICancelOrderSuccessMutationResponse }
  >({
    baseUrl: API_DOMAIN,
  }),
  tagTypes: ["Order", "PaymentOptions"],
  endpoints: (builder) => ({
    createOrder: builder.mutation<IOrder, ICreateOrderPayload>({
      queryFn: async (payload, api, _, baseQuery) => {
        const state = api.getState() as IHistoricalPaymentSliceState;
        const modifiedPayload = { ...payload };
        if (state?.historicalPaymentSlice?.historical) {
          modifiedPayload.previous_month_payment = true;
        }

        const resp = await baseQuery({
          url: "/api/v1/order",
          method: "POST",
          body: modifiedPayload,
        });

        return resp as any;
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled.then(() => {
          removeLocalItem(LocaleStorageKeysEnum.GLOBAL_COUPON);
          dispatch(couponsApi.util.invalidateTags(["ReservedClientCoupons", "ClientCoupons"]));
        });
      },
    }),

    getActiveOrder: builder.query<IOrder, void>({
      query: () => ({
        url: "/api/v1/order/active",
      }),
      providesTags: ["Order"],
    }),

    getPaymentOptions: builder.query<TGetPaymentOptions, void>({
      query: () => ({
        url: "/api/v1/order/get-payment-options",
      }),
      providesTags: ["PaymentOptions"],
    }),

    proccessPayment: builder.mutation<TProccessPaymentMutationResponse, IProcessPaymenetMutationPayload>({
      query: ({ id, paymentOption }) => ({
        url: `/api/v1/order/${id}/process-payment`,
        method: "POST",
        params: {
          option_type: paymentOption,
        },
      }),
      transformResponse: (response: { meta: ICashPaymentSuccessResponse | IMIPPaymentSuccessResponse }) => {
        return response?.meta;
      },
    }),

    payWithBanks: builder.mutation<
      IBankPaymentSuccessResponse,
      { payload: IBankPaymentPayload; method: PaymentIniatorEnum }
    >({
      query: ({ method, payload }) => ({
        url: getPaymentUrlByPaymentInitiator(method),
        method: "POST",
        body: payload,
      }),
    }),

    cancelOrder: builder.mutation<ICancelOrderSuccessMutationResponse, string>({
      query: (id) => ({
        url: `/api/v1/order/${id}/cancel`,
        method: "POST",
      }),
      transformResponse: (response: { meta: ICancelOrderSuccessMutationResponse }) => response?.meta,
    }),

    getOrderInvoice: builder.query<ArrayBuffer, void>({
      query: () => ({
        url: "/api/v1/order/active/invoice",
      }),
      extraOptions: {
        serializeResponse: false,
        pdfResponse: true,
      },
    }),

    getOrderById: builder.query<IOrder, string>({
      query: (id) => ({
        url: `/api/v1/order/${id}`,
      }),
    }),
    addClientCoupon: builder.mutation<void, string>({
      query: (couponId: string) => {
        const url = `/api/v1/order/${couponId}/add-coupon`;
        return {
          url,
          method: "PUT",
        };
      },
      invalidatesTags: ["Order"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled.then(() => {
          dispatch(couponsApi.util.invalidateTags(["ReservedClientCoupons", "ClientCoupons"]));
        });
      },
    }),
    removeClientCoupon: builder.mutation<void, string>({
      query: (couponId: string) => {
        const url = `/api/v1/order/${couponId}/remove-coupon`;
        return {
          url,
          method: "PUT",
        };
      },
      invalidatesTags: ["Order"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled.then(() => {
          dispatch(couponsApi.util.invalidateTags(["ReservedClientCoupons", "ClientCoupons"]));
        });
      },
    }),
    addGlobalCoupon: builder.mutation<void, string>({
      query: (coupon) => {
        const url = "/api/v1/order/add-promotion-coupon";
        return {
          url,
          method: "PUT",
          body: { code: coupon },
        };
      },
      invalidatesTags: ["Order"],
    }),
    removeGlobalCoupon: builder.mutation<void, void>({
      query: () => {
        const url = "/api/v1/order/remove-promotion-coupon";
        return {
          url,
          method: "DELETE",
        };
      },
      invalidatesTags: ["Order"],
    }),
  }),
});

export const {
  useCreateOrderMutation,
  useGetActiveOrderQuery,
  useLazyGetActiveOrderQuery,
  useGetOrderByIdQuery,
  useGetOrderInvoiceQuery,
  useProccessPaymentMutation,
  useGetPaymentOptionsQuery,
  useCancelOrderMutation,
  usePayWithBanksMutation,
  useAddClientCouponMutation,
  useRemoveClientCouponMutation,
  useAddGlobalCouponMutation,
  useRemoveGlobalCouponMutation,
} = orderApi;

export default orderApi.reducer;
